import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading } from "@/components/pawkt/Empty";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const canManage = (u) => u?.role === "owner" || u?.role === "admin" || (u?.effective_permissions || u?.permissions || []).includes("communications.manage") || (u?.permissions || []).includes("*");

export default function CommAutomation() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [events, setEvents] = useState(null);
  const manage = canManage(user);

  const { data, isLoading } = useQuery({ queryKey: ["comm-automation"], queryFn: async () => (await api.get("/communications/automation")).data });
  useEffect(() => { if (data) setEvents(data.events); }, [data]);

  const save = useMutation({
    mutationFn: () => api.put("/communications/automation", { events }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["comm-automation"] }); toast.success("Automation saved"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  if (isLoading || !events) return <Loading />;
  const channels = data.channels;
  const toggle = (ev, ch, val) => setEvents((s) => ({ ...s, [ev]: { ...s[ev], [ch]: val } }));

  return (
    <div data-testid="comm-automation-page">
      <PageHeader title="Communication Automation" subtitle="Choose which messages send automatically — per event, per channel">
        <Button onClick={() => save.mutate()} disabled={!manage || save.isPending} data-testid="automation-save" className="bg-slate-900 text-white">{save.isPending ? "Saving…" : "Save changes"}</Button>
      </PageHeader>
      {!manage && <div className="mb-4 flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-700"><AlertTriangle className="h-4 w-4" /> Viewing only — needs communications.manage to edit.</div>}

      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="automation-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="px-4 py-3">Event</th>{channels.map((c) => <th key={c} className="px-4 py-3 text-center capitalize">{c}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {Object.entries(events).map(([ev, chans]) => (
              <tr key={ev} data-testid={`automation-row-${ev}`}>
                <td className="px-4 py-3 font-medium text-slate-800 capitalize">{ev.replace(/_/g, " ")}</td>
                {channels.map((ch) => (
                  <td key={ch} className="px-4 py-3 text-center">
                    <Switch checked={!!chans[ch]} disabled={!manage} onCheckedChange={(v) => toggle(ev, ch, v)} data-testid={`auto-${ev}-${ch}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-slate-400">Turning a channel off stops only automatic sends — you can still send manually from any record. Consent & provider rules always apply.</p>
    </div>
  );
}
